import MobileBasicButton from 'components/Buttons/MobileBasicButton';
import MobileLeaveModal from 'components/Modal/MobileLeaveModal';
import { governmentReadyPageConstants } from 'constants/Government/governmentReadyPageConstants';
import { commonText } from 'constants/commonConstants';
import { governmentPaths } from 'constants/path';
import useToggle from 'hooks/CustomHooks/useToggle';
import { ValueType } from 'models/Common/commonTypes';
import { ChangeEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGovernmentHeaderStore } from 'store/useGovernmentHeaderStore';
import styled from 'styled-components';
import { AgreeAndCodeConfirmTitle, TitleContainer } from 'styles/Global/Government/agreeAndCodeConfirmPageStyles';
import { BackgroundColor, BorderColor } from 'styles/Global/colorSemantic';
import { mindupAddDiagnosisApiCall, updateVoucherCodeStatus } from 'functions/apiFunctions';
import { font, palette } from 'styles/Global/globalStyles';
import CheckBox from 'assets/Icons/ETC/Tablet/CheckBox';
import { changeDiagnosisStatus } from 'functions/apiFunctions';
import { DiagnosisStatus, VoucherCodeStatus } from 'utils/statusCode';
import { preciousChildPaths } from 'constants/path';
import InfoContents from './Components/InfoContents';
import { VoucherAddDiagnosisRqData } from 'models/MindUp/MindUpTypes';
import { calculateAge } from 'utils/calculateAge';
import moment from 'moment';
import { addDiagnosisApi } from 'api/DCK/addDiagnosisApi';

const InputCounselReasonPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const childInputInfo = state.value;
    const token = state.data.token;
    const voucher = state.data.voucher;
    const hospitalId = state.data.hospitalId;

    const counselReasons = [
        { id: 1, text: '기질이나 성격, 적성을 이해하기 위해' },
        { id: 2, text: '친구와의 갈등에 대한 원인 파악을 위해' },
        { id: 3, text: '문제 행동에 대한 원인 파악을 위해' },
        { id: 4, text: '자녀의 마음을 이해하기 위해' },
        { id: 5, text: '주변의 권유로' },
        { id: 6, text: '기타' },
    ];

    const [selectedReason, setSelectedReason] = useState<number | null>(null);
    const [otherReason, setOtherReason] = useState<string>('');

    const [value] = useState<ValueType>({
        patientName: childInputInfo.patientName,
        birthDate: `${childInputInfo.birthDate}`,
        gender: childInputInfo.gender,
        schoolAge: childInputInfo.schoolAge,
        semester: childInputInfo.semester,
        grade: childInputInfo.grade,
        voucherCode: childInputInfo.voucherCode,
        parentName: childInputInfo.parentName,
        patientMobile: childInputInfo.patientMobile,
        relationType: childInputInfo.relationType,
    });

    const [buttonDisabled, setButtonDisabled] = useState(true);

    const { setHeaderText, setInit } = useGovernmentHeaderStore();
    const [isLeaveModal, setIsLeaveModal] = useToggle();
    const [isShowSubmitAlert, setIsShowSubmitAlert] = useToggle();

    useEffect(() => {
        setHeaderText(governmentReadyPageConstants.headerText);
    }, []);

    // Next button disabled check
    useEffect(() => {
        const checkButtonDisabled = () => {
            // 검사 동기가 선택되지 않은 경우
            if (!selectedReason) {
                return setButtonDisabled(true);
            }

            // 기타 항목이 선택되었고 입력 내용이 없는 경우
            if (selectedReason === 6 && !otherReason) {
                return setButtonDisabled(true);
            }

            return setButtonDisabled(false);
        };

        checkButtonDisabled();
    }, [selectedReason, otherReason]);

    const handleReasonSelect = (id: number) => {
        if (selectedReason === id) {
            setSelectedReason(null);
        } else {
            setSelectedReason(id);
            if (id !== 6) {
                setOtherReason('');
            }
        }
    };

    const handleOtherReasonChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setOtherReason(e.target.value);
    };

    const clickButton = async () => {
        if (!selectedReason) return;

        const counselReasonText = selectedReason === 6 ? `${counselReasons[selectedReason - 1].text} - ${otherReason}` : counselReasons[selectedReason - 1].text;

        try {
            const identityNumber = value.birthDate.toString().substring(2, 8) + value.gender;
            const hospitalInfoResponse = await addDiagnosisApi.hospitalFindById(hospitalId, token);

            const updatedValue = {
                patientName: value.patientName,
                parentName: value.parentName,
                patientMobile: value.patientMobile,
                patientIdentityNumber: identityNumber,
                patientGender: calculateAge(identityNumber).gender,
                gradeCode: value.grade,
                gradeName: value.schoolAge + ' ' + value.semester,
                relationType: Number(value.relationType),
                hospitalId: hospitalId,
                hospitalName: hospitalInfoResponse.data.name ?? '',
                doctorName: '',
                patientMemo: value.patientMemo,
                diagnosisDateTime: moment().format('YYYYMMDD') + moment().format('HHmm'),
                diagnosisTimestamp: moment().valueOf(),
                voucherCode: value.voucherCode,
                counselReason: counselReasonText,
            } as VoucherAddDiagnosisRqData;

            const diagnosisData = await mindupAddDiagnosisApiCall(updatedValue, token, hospitalId);
            await changeDiagnosisStatus(diagnosisData._id, DiagnosisStatus.inProgress, token);

            // 바우처 코드 사용 상태 업데이트
            await updateVoucherCodeStatus(updatedValue.voucherCode ?? '', VoucherCodeStatus.USED, token);
            navigate(preciousChildPaths.inspectionGuide, { replace: true, state: { token: token, diagnosis: diagnosisData, voucher: voucher } });
        } catch (error) {
            console.error('API 호출 실패:', error);
            // 롤백처리 필요...
        }
    };

    // modal 나가기 버튼
    const clickLeaveButton = () => {
        setInit();
        setIsLeaveModal();
        navigate(governmentPaths.agree);
    };

    return (
        <Layout>
            {isShowSubmitAlert && (
                <MobileLeaveModal
                    title='이후에는 수정할 수 없습니다.'
                    subTitle='이대로 검사를 시작할까요?'
                    firstButton='아니요'
                    secondButton='검사 시작'
                    secondButtonType='fill'
                    infoContentsPadding='0px'
                    infoContentsBorderColor={BackgroundColor.color_background_primary_neutral}
                    infoContentsBorderRadius='8px'
                    infoContents={<InfoContents clientInfo={value} selectedReasonIdx={selectedReason} counselReasons={counselReasons} otherReason={otherReason} />}
                    setIsLeaveModal={setIsShowSubmitAlert}
                    clickLeaveButtonInLeaveModal={clickButton}
                />
            )}
            {isLeaveModal && (
                <MobileLeaveModal
                    title={governmentReadyPageConstants.modalTitle1}
                    subTitle={governmentReadyPageConstants.modalTitle2}
                    content={governmentReadyPageConstants.modalContent}
                    setIsLeaveModal={setIsLeaveModal}
                    clickLeaveButtonInLeaveModal={clickLeaveButton}
                    firstButton={commonText.no}
                    secondButton={commonText.out}
                />
            )}
            <TitleAndWarningContainer>
                <TitleContainer>
                    <AgreeAndCodeConfirmTitle>맞춤 상담을 위해</AgreeAndCodeConfirmTitle>
                    <AgreeAndCodeConfirmTitle>검사 동기를 선택해주세요.</AgreeAndCodeConfirmTitle>
                </TitleContainer>
            </TitleAndWarningContainer>
            <BodyContainer>
                <GapContainer>
                    <ReasonAnswerContainer>
                        {counselReasons.map((reason, index) => (
                            <ReasonAnswer key={reason.id} isSelected={selectedReason === reason.id} onClick={() => handleReasonSelect(reason.id)}>
                                {selectedReason === reason.id ? <CheckBox /> : <UnCheckButton />}
                                <AnswerLabel>{reason.text}</AnswerLabel>
                            </ReasonAnswer>
                        ))}
                    </ReasonAnswerContainer>
                    {selectedReason === 6 && (
                        <TextAreaWrapper>
                            <TextArea value={otherReason} onChange={handleOtherReasonChange} placeholder='기타 이유를 작성해주세요.' />
                        </TextAreaWrapper>
                    )}
                </GapContainer>
            </BodyContainer>
            <MobileBasicButton onClick={setIsShowSubmitAlert} $type='fill' disabled={buttonDisabled}>
                {'입력 완료하기'}
            </MobileBasicButton>
        </Layout>
    );
};

export default InputCounselReasonPage;

const Layout = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0px 16px;
    gap: 32px;
`;

const TitleAndWarningContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 20px;
`;

const BodyContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 36px;
`;

const ReasonAnswerContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    border-radius: 8px;
    font-size: ${font.size[16]};
    font-weight: ${font.weight.semiBold};
`;

const ReasonAnswer = styled.div<{
    isSelected: boolean;
}>`
    display: flex;
    height: 56px;
    padding: 16px;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 8px;
    border: 1px solid ${BorderColor.color_border_light};
    background-color: ${(props) => (props.isSelected ? '#406CC533' : BackgroundColor.color_background_white)};
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
        background-color: ${(props) => (props.isSelected ? '#406CC533' : BackgroundColor.color_background_primary_neutral)};
    }
`;

const AnswerLabel = styled.label`
    font-size: ${font.size[16]};
    font-weight: ${font.weight.regular};
    width: 100%;
    padding: 16px;
    border-radius: 8px;
`;

export const relationType = {
    mother: '0',
    father: '1',
    etc: '2',
} as const;

const TextAreaWrapper = styled.div`
    width: 100%;
    margin-top: 6px;
    border-color: #333438;
`;

const GapContainer = styled.div``;

const TextArea = styled.textarea`
    display: flex;
    width: 100%;
    height: 124px;
    padding: 14px 16px;
    border: 1px solid #dbdbdb;
    border-radius: 8px;
    flex-wrap: wrap;
    resize: none;
    font-size: ${font.size[16]};
    font-weight: ${font.weight.medium};
    &::placeholder {
        color: #999;
    }
`;

const UnCheckButton = styled.button<{ isSelected?: boolean }>`
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid ${(props) => (props.isSelected ? palette.primaryNormal : '#999')};
    background-color: ${(props) => (props.isSelected ? palette.primaryNormal : 'transparent')};
`;
